import { Film, Search, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { listMovies } from "../services/FilmeService";
import { Filme } from "../models/Filme";
import { generoLabel, GeneroLabelMap } from "../lib/GeneroLabel";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { GeneroEnum } from "../models/enums/GeneroEnum";

const MovieList = () => {
  const [movies, setMovies] = useState<Filme[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGenre, setSelectedGenre] = useState<string>("ALL");

  const genres = Object.keys(GeneroLabelMap)
    .map(Number)
    .filter((v) => !isNaN(v)) as GeneroEnum[];

  useEffect(() => {
    const fetchMovies = async () => {
      try {
        setLoading(true);

        const generoParam: GeneroEnum | undefined =
          selectedGenre === "ALL"
            ? undefined
            : (Number(selectedGenre) as GeneroEnum);

        const data = await listMovies(searchTerm, generoParam);
        setMovies(data.items);
      } catch {
        setMovies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
  }, [searchTerm, selectedGenre]);

  const renderStars = (rating: number) => (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < rating
              ? "fill-accent text-accent"
              : "text-notebook-line"
          }`}
        />
      ))}
    </div>
  );

  return (
    <section className="min-h-screen bg-muted py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="flex justify-center mb-4">
            <Film className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-romantic text-5xl md:text-6xl text-foreground mb-4">
            Nossos Filmes Favoritos
          </h2>
          <p className="font-serif text-xl text-muted-foreground">
            Histórias que assistimos juntos
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar filme..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/80 border-primary/20 focus:border-primary"
            />
          </div>

          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-full sm:w-48 bg-white/80 border-primary/20">
              <SelectValue placeholder="Gênero" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="ALL">Todos os gêneros</SelectItem>

              {genres.map((genre) => (
                <SelectItem key={genre} value={String(genre)}>
                  {GeneroLabelMap[genre]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notebook */}
        <div className="bg-notebook-bg rounded-xl shadow-2xl p-8 md:p-12 border-l-4 border-primary relative">
          {/* Linhas */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="h-10 border-b border-notebook-line/30"
              />
            ))}
          </div>

          <div className="relative space-y-8">
            {loading && (
              <p className="text-center font-serif text-muted-foreground">
                Carregando filmes...
              </p>
            )}

            {!loading && movies.length === 0 && (
              <p className="text-center font-serif text-muted-foreground">
                Nenhum filme cadastrado ainda ♡
              </p>
            )}

            {movies.map((movie, index) => (
              <div
                key={movie.id}
                className="animate-fade-in bg-white/60 backdrop-blur-sm rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 border-primary/10"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl font-semibold text-notebook-text mb-1">
                      {movie.nome}
                    </h3>

                    <p className="text-sm font-serif text-muted-foreground">
                      {generoLabel(movie.genero)}
                    </p>
                  </div>

                  <div className="flex items-center gap-5">
                    <span className="text-lg font-serif text-notebook-text">
                      Nota:
                    </span>
                    <div className="scale-125">
                      {renderStars(movie.nota)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t-2 border-dashed border-notebook-line/50">
            <p className="font-romantic text-2xl text-center text-primary">
              E muitos outros ainda virão... ♡
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MovieList;
